import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ImageUploadField, PageHeader, useToast } from 'components/admin';
import { Button, Checkbox, Input, Select } from 'components/common';
import { createProjectInProgress, getAdminProjectInProgressById, updateProjectInProgress } from 'apis/adminProjectsInProgress';
import { getAdminProjectInformationTypes } from 'apis/adminProjectInformationTypes';
import { getAdminEcosystemGroups } from 'apis/adminEcosystemGroups';
import ProjectInformationItemsEditor from '../shared/ProjectInformationItemsEditor';
import {
    useActiveLanguagesQuery,
    useAdminCreateMutation,
    useAdminDetailQuery,
    useAdminListQuery,
    useAdminUpdateMutation,
} from 'hooks';
import '../../../styles/admin-shared.scss';

const createInformationItem = (languages, sortOrder = 1) => ({ projectInformationTypeId: '', sortOrder, isActive: true, translation: languages.map((language) => ({ languageId: Number(language.id), value: '' })) });
const normalizeInformationItems = (items, languages) => (items || []).map((item) => {
    const translations = item.translation || item.translations || [];
    return {
        ...item,
        projectInformationTypeId: item.projectInformationTypeId ?? item.ProjectInformationTypeId ?? '',
        sortOrder: Number(item.sortOrder ?? item.SortOrder) || 0,
        isActive: item.isActive ?? item.IsActive ?? true,
        translation: languages.map((language) => {
            const match = translations.find((entry) => Number(entry.languageId ?? entry.LanguageId) === Number(language.id));
            return { ...match, languageId: Number(language.id), value: match?.value ?? match?.Value ?? '' };
        }),
    };
}).sort((first, second) => first.sortOrder - second.sortOrder);

const ProjectsInProgressForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState(0);
    const [formData, setFormData] = useState({ imageUrl: '', websiteUrl: '', ecosystemGroupId: '', sortOrder: 0, isActive: true, translation: [], informationItems: [] });

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: typeData = { items: [] } } = useAdminListQuery('project-information-types-options', { pageSize: 1000, languageCode: 'vi', isActive: true }, getAdminProjectInformationTypes);
    const types = typeData.items;
    const { data: ecosystemData = { items: [] } } = useAdminListQuery('project-ecosystem-options', { pageSize: 100, languageCode: 'vi', isActive: true }, getAdminEcosystemGroups);
    const ecosystemOptions = ecosystemData.items.map((group) => ({ value: group.id ?? group.Id, label: group.name ?? group.Name ?? group.title ?? group.Title ?? `Nhóm #${group.id ?? group.Id}` }));
    const { data: detail, error: detailError } = useAdminDetailQuery('projects-in-progress', id, getAdminProjectInProgressById);
    const createMutation = useAdminCreateMutation('projects-in-progress', createProjectInProgress);
    const updateMutation = useAdminUpdateMutation('projects-in-progress', updateProjectInProgress);
    const loading = createMutation.isPending || updateMutation.isPending;

    useEffect(() => {
        if (!id && languages.length) {
            setFormData(prev => ({ ...prev, translation: languages.map(l => ({ languageId: l.id, name: '', description: '' })), informationItems: prev.informationItems.length ? prev.informationItems : [createInformationItem(languages)] }));
        }
    }, [id, languages]);

    useEffect(() => {
        if (detail && languages.length) setFormData({ ...detail, websiteUrl: detail.websiteUrl ?? detail.WebsiteUrl ?? '', ecosystemGroupId: detail.ecosystemGroupId ?? detail.EcosystemGroupId ?? '', informationItems: normalizeInformationItems(detail.informationItems || detail.InformationItems, languages) });
    }, [detail, languages]);

    useEffect(() => {
        if (detailError) toast.error(detailError.message || 'Tải dữ liệu thất bại');
    }, [detailError, toast]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };
    const handleTranslationChange = (langId, field, value) => {
        setFormData(prev => ({ ...prev, translation: prev.translation.map(t => Number(t.languageId) === Number(langId) ? { ...t, [field]: value } : t) }));
    };
    const handleInformationItemChange = (index, field, value) => setFormData((previous) => ({ ...previous, informationItems: previous.informationItems.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item) }));
    const handleInformationTranslationChange = (index, languageId, value) => setFormData((previous) => ({ ...previous, informationItems: previous.informationItems.map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        const exists = item.translation.some((translation) => Number(translation.languageId) === Number(languageId));
        return { ...item, translation: exists ? item.translation.map((translation) => Number(translation.languageId) === Number(languageId) ? { ...translation, value } : translation) : [...item.translation, { languageId: Number(languageId), value }] };
    }) }));
    const addInformationItem = () => setFormData((previous) => ({ ...previous, informationItems: [...previous.informationItems, createInformationItem(languages, Math.max(0, ...previous.informationItems.map((item) => Number(item.sortOrder) || 0)) + 1)] }));
    const removeInformationItem = (index) => setFormData((previous) => ({ ...previous, informationItems: previous.informationItems.filter((_, itemIndex) => itemIndex !== index) }));
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...formData, websiteUrl: formData.websiteUrl || null, ecosystemGroupId: formData.ecosystemGroupId || null, sortOrder: Number(formData.sortOrder), informationItems: formData.informationItems.map((item) => ({ projectInformationTypeId: Number(item.projectInformationTypeId), sortOrder: Number(item.sortOrder) || 0, isActive: Boolean(item.isActive), translation: item.translation.map((translation) => ({ languageId: Number(translation.languageId), value: translation.value || '' })) })) };
            const res = id
                ? await updateMutation.mutateAsync({ id, data: payload })
                : await createMutation.mutateAsync(payload);
            if (res.success) { toast.success(id ? 'Cập nhật thành công' : 'Thêm mới thành công'); navigate('/admin/projects-in-progress'); }
            else { if (res.messageError) setErrors(res.messageError); else toast.error(res.message || 'Lưu thất bại'); }
        } catch { toast.error('Lưu thất bại'); }
    };

    return (
        <div className="admin-page">
            <PageHeader title={id ? 'Cập nhật Dự án' : 'Thêm Dự án mới'} subtitle="Thông tin dự án đang triển khai" />
            <form className="form-card" onSubmit={handleSubmit}>
                <p className="form-section-title">Chi tiết Dự án</p>
                <ImageUploadField
                    label="Ảnh bìa dự án"
                    value={formData.imageUrl}
                    folder="projects/in-progress"
                    onChange={(imageUrl) => setFormData(prev => ({ ...prev, imageUrl }))}
                />
                <Select label="Hệ sinh thái" name="ecosystemGroupId" value={formData.ecosystemGroupId || ''} onChange={handleChange} options={ecosystemOptions} placeholder="Chọn hệ sinh thái" />
                <Input label="URL Trang chủ (Ví dụ: https://benhvienbacmy.vn)" name="websiteUrl" value={formData.websiteUrl || ''} onChange={handleChange} placeholder="https://..." />
                <Input label="Thứ tự hiển thị" name="sortOrder" type="number" value={formData.sortOrder} onChange={handleChange} />
                <Checkbox label="Hoạt động" name="isActive" checked={formData.isActive} onChange={handleChange} />

                {languages.length > 0 && (
                    <>
                        <p className="form-section-title">Bản dịch</p>
                        <div className="translation-tabs">
                            <div className="tab-header">{languages.map((lang, i) => <button key={lang.id} type="button" className={`tab-btn ${activeTab === i ? 'active' : ''}`} onClick={() => setActiveTab(i)}>{lang.name}</button>)}</div>
                            {languages.map((lang, i) => {
                                const t = formData.translation.find(t => Number(t.languageId) === Number(lang.id)) || {};
                                return activeTab === i ? (
                                    <div key={lang.id} className="tab-content">
                                        <Input label="Tên dự án *" value={t.name || ''} onChange={e => handleTranslationChange(lang.id, 'name', e.target.value)} required />
                                        <ProjectInformationItemsEditor items={formData.informationItems} languages={languages} activeTab={activeTab} types={types} onItemChange={handleInformationItemChange} onTranslationChange={handleInformationTranslationChange} onAdd={addInformationItem} onRemove={removeInformationItem} />
                                    </div>
                                ) : null;
                            })}
                        </div>
                    </>
                )}
                <div className="form-actions">
                    <Button type="submit" disabled={loading}>{loading ? 'Đang lưu...' : id ? 'Cập nhật Dự án' : 'Thêm Dự án'}</Button>
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/projects-in-progress')}>Hủy</Button>
                </div>
            </form>
        </div>
    );
};

export default ProjectsInProgressForm;
